/**
 * Leaderboard ranking (#144). One implementation for the public leaderboard
 * (calculate-leaderboard), the admin Top 6 review, the Needs review list and
 * the publish checklist, so they can never rank catches differently.
 * Moved from amplify/functions/calculate-leaderboard/rules.ts unchanged.
 *
 * #165 batch A (A2, A3): the tie rule and the length sanity check both live
 * here so every caller of buildCategoryRankings gets them for free,
 * including shared/adjudication.ts's computeRanks.
 */

export const CATEGORIES = ['FLATHEAD', 'BREAM', 'JEWFISH', 'MANGROVE_JACK', 'WHITING', 'TAILOR', 'TREVALLY'];

/** The admin review and the publish checklist look at this many places per category (club rule, 19 Sep 2026). */
export const PLACED_RANK_LIMIT = 6;

/**
 * #165 A3 (F-09, F-18): the submission-time cap (shared/catchLength.ts) is
 * not re-checked at read time, so a legacy or manually-edited row over this
 * length still ranks. 200 is inclusive; anything over it, or any value that
 * isn't a finite positive number, is excluded here instead.
 */
export const MAX_CATCH_LENGTH_CM = 200;

/**
 * True only for a finite number greater than 0 and at most
 * MAX_CATCH_LENGTH_CM. NaN, null, undefined, strings, 0, negatives and
 * anything over the cap are all false. 200 itself is true; 200.1 is false.
 */
export function isRankableLength(v: unknown): boolean {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= MAX_CATCH_LENGTH_CM;
}

export interface CatchEntry {
  id: string;
  memberSub?: string;
  memberId?: string;
  participantEmail?: string;
  competitorName?: string;
  species: string;
  lengthCm: number;
  isMysteryFish?: boolean;
  /** A2 tie rule: earliest wins. Missing/invalid (not a parseable ISO date) sorts after every valid one. */
  timeOfCatch?: string | null;
  /** A2 tie rule: second tiebreak after timeOfCatch. Missing/invalid sorts after every valid one. */
  createdAt?: string | null;
}

/** An excluded-from-ranking catch, shown to admins only (A3). */
export interface ExcludedCatch {
  id: string;
  competitorName: string | undefined;
  species: string;
  lengthCm: number;
  reason: 'invalid length';
}

/**
 * The key that identifies ONE competitor when picking their best catch.
 *
 * Order is deliberate: memberSub, then participantEmail, are unchanged from
 * before, so existing grouping is identical for members with a login and for
 * guests. memberId is only reached when both are absent, which is the
 * no-login family member case (backlog #107: their catches have no memberSub
 * and, being member catches, no participantEmail). Without it every such
 * catch fell through to `id`, so one person could take several places in a
 * category. `id` remains the last resort.
 */
export function competitorKey(c: { id: string; memberSub?: string | null; memberId?: string | null; participantEmail?: string | null }): string {
  return c.memberSub || c.participantEmail || c.memberId || c.id;
}

export interface RankedEntry {
  rank: number;
  competitorName: string | undefined;
  species: string;
  lengthCm: number;
  submissionId: string;
  isPrizeWinner: boolean;
}

/**
 * Ranks the given catches (callers pass APPROVED catches only) per category,
 * best catch per competitor.
 *
 * A3: a catch whose lengthCm is not rankable (see isRankableLength) never
 * enters a ranking, never wins its competitor the "best catch" comparison,
 * and is listed in `excluded` instead (admin-only display — see A3's
 * "excluded: invalid length" badge).
 *
 * A2 tie rule (decided 17 Oct 2026): ties do NOT share a place. Order is
 * longest lengthCm first; if equal, earliest valid timeOfCatch; if still
 * equal, earliest valid createdAt; final fallback is id ascending, so the
 * order is deterministic and independent of input/database order. A missing
 * or unparseable timeOfCatch (or createdAt) sorts AFTER every valid one —
 * never treated as earliest, so a competitor can't win a tie by leaving the
 * field blank.
 */
export function buildCategoryRankings(catches: CatchEntry[]): Record<string, RankedEntry[]> {
  const rankings: Record<string, RankedEntry[]> = {};
  for (const cat of CATEGORIES) {
    const catCatches = catches.filter(c => c.species === cat && isRankableLength(c.lengthCm));
    const bestPerCompetitor: Record<string, CatchEntry> = {};
    for (const c of catCatches) {
      const key = competitorKey(c);
      const current = bestPerCompetitor[key];
      if (!current || compareForTie(c, current) < 0) {
        bestPerCompetitor[key] = c;
      }
    }
    rankings[cat] = Object.values(bestPerCompetitor)
      .sort(compareForTie)
      .map((entry, idx) => ({
        rank: idx + 1,
        competitorName: entry.competitorName,
        species: entry.species,
        lengthCm: entry.lengthCm,
        submissionId: entry.id,
        isPrizeWinner: idx < 3,
      }));
  }
  return rankings;
}

/** The excluded (non-rankable-length) catches, admin-only (A3). */
export function excludedCatches(catches: CatchEntry[]): ExcludedCatch[] {
  return catches
    .filter(c => CATEGORIES.includes(c.species) && !isRankableLength(c.lengthCm))
    .map(c => ({
      id: c.id,
      competitorName: c.competitorName,
      species: c.species,
      lengthCm: c.lengthCm,
      reason: 'invalid length' as const,
    }));
}

/**
 * A valid-ISO-date instant for tie ordering, or null when missing/
 * unparseable — null always sorts after every non-null value.
 */
function tieInstant(v: string | null | undefined): number | null {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

/** The A2 tie rule's total order: longest length, then earliest timeOfCatch, then earliest createdAt, then id ascending. */
function compareForTie(a: CatchEntry, b: CatchEntry): number {
  if (a.lengthCm !== b.lengthCm) return b.lengthCm - a.lengthCm;

  const aTime = tieInstant(a.timeOfCatch);
  const bTime = tieInstant(b.timeOfCatch);
  if (aTime === null && bTime !== null) return 1;
  if (aTime !== null && bTime === null) return -1;
  if (aTime !== null && bTime !== null && aTime !== bTime) return aTime - bTime;

  const aCreated = tieInstant(a.createdAt);
  const bCreated = tieInstant(b.createdAt);
  if (aCreated === null && bCreated !== null) return 1;
  if (aCreated !== null && bCreated === null) return -1;
  if (aCreated !== null && bCreated !== null && aCreated !== bCreated) return aCreated - bCreated;

  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** catchId -> rank within its category, for every ranked catch. */
export function rankByCatchId(rankings: Record<string, RankedEntry[]>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const entries of Object.values(rankings)) for (const e of entries) out[e.submissionId] = e.rank;
  return out;
}
